import React, { useState } from "react";
import { Plus, RefreshCw } from "lucide-react";
import Task from "./Task";
import { Task as TaskType, WalletSettings } from "@/utils/storage";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { DeleteConfirmationDialog } from "../dialogs";

interface TaskListProps {
  tasks: TaskType[];
  editMode: boolean;
  isAnimating: boolean;
  onComplete: (task: TaskType) => void;
  onEdit?: (task: TaskType) => void;
  onDelete?: (id: string) => void;
  onMoveTask?: (id: string, direction: "up" | "down") => void;
  onUpdateStars?: (taskId: string, change: number) => void;
  onTaskReset?: (task: TaskType) => void;
  onAddNew: () => void;
  onResetDaily: () => void;
  walletSettings?: WalletSettings;
}

const TaskList: React.FC<TaskListProps> = ({
  tasks,
  editMode,
  isAnimating,
  onComplete,
  onEdit,
  onDelete,
  onMoveTask,
  onUpdateStars,
  onTaskReset,
  onAddNew,
  onResetDaily,
  walletSettings,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<TaskType | null>(null);

  const handleDeleteClick = (task: TaskType) => {
    setTaskToDelete(task);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (taskToDelete && onDelete) {
      onDelete(taskToDelete.id);
    }
    setDeleteDialogOpen(false);
    setTaskToDelete(null);
  };

  // Calculate completed tasks for today
  const completedToday = tasks.filter(
    (task) =>
      task.completed &&
      task.completedDate &&
      new Date(task.completedDate).toDateString() === new Date().toDateString()
  ).length;

  return (
    <>
      {/* Header with task progress */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">✅ Tasks</div>
            <div className="flex items-center gap-4">
              <div className="text-sm font-normal text-muted-foreground">
                {completedToday}/{tasks.length}
              </div>
              {editMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onAddNew}
                  className="flex items-center gap-1"
                >
                  <Plus size={16} />
                  Add Task
                </Button>
              )}
            </div>
          </CardTitle>
        </CardHeader>
      </Card>

      <div className="space-y-4">
        {tasks.length === 0 ? (
          <div className="text-center p-8 glassmorphism rounded-2xl">
            <p className="text-muted-foreground">No tasks yet</p>
            {editMode && (
              <Button
                variant="outline"
                size="sm"
                onClick={onAddNew}
                className="mt-2"
              >
                Add your first task
              </Button>
            )}
          </div>
        ) : (
          tasks.map((task) => (
            <div key={task.id} id={`task-${task.id}`}>
              <Task
                task={task}
                onComplete={onComplete}
                onEdit={editMode ? onEdit : undefined}
                onDelete={editMode ? () => handleDeleteClick(task) : undefined}
                onMoveUp={
                  editMode
                    ? (id) => onMoveTask && onMoveTask(id, "up")
                    : undefined
                }
                onMoveDown={
                  editMode
                    ? (id) => onMoveTask && onMoveTask(id, "down")
                    : undefined
                }
                onUpdateStars={editMode ? onUpdateStars : undefined}
                onTaskReset={onTaskReset}
                editMode={editMode}
                isAnimating={isAnimating}
                walletSettings={walletSettings}
              />
            </div>
          ))
        )}
      </div>

      <div className="mt-6 flex justify-center">
        <Button
          variant="outline"
          size="sm"
          onClick={onResetDaily}
          className="flex items-center gap-1"
        >
          <RefreshCw size={16} />
          Reset Daily Tasks
        </Button>
      </div>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        itemType="task"
        itemName={taskToDelete?.title}
      />
    </>
  );
};

export default TaskList;
